/**
 * pmsSelectors — pure helpers over pmsDemoData.
 *
 * No state, no side-effects, no network. When we swap the demo dataset for a
 * real API, these selectors keep the same signatures so screens keep working.
 *
 * All date arithmetic uses YYYY-MM-DD strings (UTC-agnostic). Dates outside
 * August 2028 are handled correctly by the helpers even though the demo
 * dataset only declares that month.
 */
import {
  VILLAS, POOLS, CHANNELS,
  MONTHLY_STATEMENTS, DEMO_TODAY_ISO, DEMO_MONTH,
  PROPERTY_ISSUES, YTD_STATS_2028, SPECIAL_PERIODS,
  DEMO_DOKU_CARD, DEMO_IDR_PER_EUR, OPERATING_EXPENSES_ESTIMATED,
} from "@/data/pmsDemoData";
import {
  getBlocks, getReservations, getOwnerStayRequests, effectiveBlockEnd,
} from "@/lib/pmsRuntimeStore";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatEUR(n) {
  const v = Number(n) || 0;
  return "€" + v.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function formatEURDecimal(n) {
  const v = Number(n) || 0;
  return "€" + v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Símbolos de las monedas que Ajustes deja elegir. */
const CURRENCY_SYMBOL = { EUR: "\u20ac", USD: "$", AUD: "A$", SGD: "S$" };

/**
 * Importe en la moneda que diga la configuración, sin convertir nada.
 *
 * La vista previa NO hace conversión de divisa: el número se enseña tal como
 * está guardado y solo cambia la etiqueta. Mezclar monedas con un tipo de
 * cambio inventado daría cifras falsas, así que quien llame a esto debe pasar
 * la moneda en la que ya está el importe.
 */
export function formatMoney(n, currency = "EUR", { decimals = 0 } = {}) {
  const v = Number(n) || 0;
  const body = v.toLocaleString("es-ES", {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
  const symbol = CURRENCY_SYMBOL[currency];
  return symbol ? symbol + body : body + " " + currency;
}

/**
 * Igual que `formatMoney`, pero sobre la forma explícita `{ amount, currency }`.
 *
 * Al recibir el par entero no hay forma de pintar un importe con una moneda
 * que no sea la suya: el error de reetiquetar euros como dólares deja de ser
 * expresable.
 */
export function formatMoneyValue(money, opts) {
  return formatMoney(money?.amount, money?.currency, opts);
}

export function formatPct(fraction, digits = 1) {
  const v = Number(fraction) || 0;
  return (v * 100).toFixed(digits) + " %";
}

// ---------------------------------------------------------------------------
// Date utilities (pure)
// ---------------------------------------------------------------------------

/** "2028-08-21" → Date at UTC midnight. */
export function parseISO(iso) {
  if (!iso) return null;
  return new Date(iso + "T00:00:00Z");
}

/** Date → "YYYY-MM-DD" using UTC to avoid TZ off-by-one. */
export function toISO(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso, delta) {
  const d = parseISO(iso);
  d.setUTCDate(d.getUTCDate() + delta);
  return toISO(d);
}

/** Inclusive-inclusive list of ISO dates from `start` up to (not including) `endExclusive`. */
export function datesBetweenExclusive(startISO, endExclusiveISO) {
  const out = [];
  let cur = startISO;
  while (cur < endExclusiveISO) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

/** Number of nights slept: check_out − check_in. */
export function nightsBetween(checkInISO, checkOutISO) {
  const a = parseISO(checkInISO).getTime();
  const b = parseISO(checkOutISO).getTime();
  return Math.max(0, Math.round((b - a) / 86400000));
}

/** Number of nights of `[check_in, check_out)` that fall inside `monthPrefix` ("2028-08"). */
export function nightsInMonth(checkInISO, checkOutISO, monthPrefix) {
  const nights = datesBetweenExclusive(checkInISO, checkOutISO);
  return nights.filter((d) => d.startsWith(monthPrefix)).length;
}

/** True when `iso` is a night included in the reservation stay `[check_in, check_out)`. */
export function isNightInStay(iso, checkInISO, checkOutISO) {
  return iso >= checkInISO && iso < checkOutISO;
}

/**
 * True when `iso` is a day covered by the availability block. Respeta el
 * `effectiveBlockEnd` — cuando un block runtime está resuelto, no bloquea
 * fechas posteriores a la resolución.
 */
export function isDayInBlock(iso, block) {
  const end = effectiveBlockEnd(block);
  return iso >= block.start_date && iso <= end;
}

/** Fecha efectiva de fin del block — reexport para consumidores del selector. */
export { effectiveBlockEnd };

/** First and last ISO date of a monthPrefix ("2028-08"). */
export function monthRange(monthPrefix) {
  const [y, m] = monthPrefix.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  return { firstISO: toISO(first), lastISO: toISO(last), days: last.getUTCDate() };
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function getVilla(villaId) {
  return VILLAS.find((v) => v.villa_id === villaId) || null;
}

export function getPool(poolId) {
  return POOLS.find((p) => p.pool_id === poolId) || null;
}

export function getChannel(key) {
  return CHANNELS[key] || { key, label: key, commission_pct: 0, kind: "direct" };
}

export function getReservation(reservationId) {
  return getReservations().find((r) => r.reservation_id === reservationId) || null;
}

export function villasByPool(poolId) {
  return VILLAS.filter((v) => v.pool_id === poolId);
}

// ---------------------------------------------------------------------------
// Reservations & blocks selectors
// ---------------------------------------------------------------------------

/** All confirmed OR pending reservations (excludes cancelled). */
export function activeReservations() {
  return getReservations().filter((r) => r.booking_status !== "cancelled");
}

/** All reservations (any status) touching the given month. */
export function reservationsInMonth(monthPrefix = DEMO_MONTH) {
  return getReservations().filter((r) => nightsInMonth(r.check_in, r.check_out, monthPrefix) > 0);
}

/** Active reservations for a specific villa, ordered by check_in. */
export function reservationsForVilla(villaId) {
  return activeReservations()
    .filter((r) => r.assigned_villa_id === villaId)
    .sort((a, b) => (a.check_in < b.check_in ? -1 : 1));
}

/**
 * Blocks para una villa. Lee del runtime store (seed + creados en sesión).
 * Los componentes que necesiten reactividad deben además llamar a
 * `usePmsBlocks()` para suscribirse al store.
 */
export function blocksForVilla(villaId) {
  return getBlocks().filter((b) => b.villa_id === villaId);
}

// ---------------------------------------------------------------------------
// Villa-state on a given day
// ---------------------------------------------------------------------------

/**
 * Order matters: arrival-today > cleaning > occupied > owner > maintenance
 * > available. Cleaning is inferred when a checkout happened today morning
 * and no new arrival takes over the same night.
 */
export function getVillaStateOn(villaId, iso = DEMO_TODAY_ISO) {
  // Blocks first — they mark the villa clearly.
  const blocks = blocksForVilla(villaId).filter((b) => isDayInBlock(iso, b));
  const ownerStay = blocks.find((b) => b.type === "OWNER_STAY");
  const maint = blocks.find((b) => b.type === "MAINTENANCE");
  const adminBlk = blocks.find((b) => b.type === "ADMIN_BLOCK");

  const reservations = reservationsForVilla(villaId);
  const arrivingToday = reservations.find((r) => r.check_in === iso);
  const inStay = reservations.find((r) => isNightInStay(iso, r.check_in, r.check_out));
  const leavingToday = reservations.find((r) => r.check_out === iso);

  if (arrivingToday) return { state: "ARRIVAL_TODAY", reservation: arrivingToday };
  if (inStay) return { state: "OCCUPIED", reservation: inStay };
  if (ownerStay) return { state: "OWNER_STAY", block: ownerStay };
  if (maint) return { state: "MAINTENANCE", block: maint };
  if (adminBlk) return { state: "ADMIN_BLOCK", block: adminBlk };
  if (leavingToday) return { state: "CLEANING", reservation: leavingToday };
  return { state: "AVAILABLE" };
}

/**
 * State per villa for a specific date — [{villa, state, reservation?, block?}].
 */
export function villaGridOn(iso = DEMO_TODAY_ISO) {
  return VILLAS.map((v) => ({ villa: v, ...getVillaStateOn(v.villa_id, iso) }));
}

/** Reservations checking in on `iso`. */
export function arrivalsOn(iso = DEMO_TODAY_ISO) {
  return activeReservations().filter((r) => r.check_in === iso);
}

/** Reservations checking out on `iso`. */
export function departuresOn(iso = DEMO_TODAY_ISO) {
  return activeReservations().filter((r) => r.check_out === iso);
}

/** Cleaning tasks: any villa whose reservation checked out today (unless a new arrival covers it). */
export function cleaningTasksOn(iso = DEMO_TODAY_ISO) {
  return VILLAS
    .map((v) => ({ villa: v, ...getVillaStateOn(v.villa_id, iso) }))
    .filter((row) => row.state === "CLEANING");
}

/** Villas currently occupied on `iso` (guest inside for the night). */
export function occupiedVillasOn(iso = DEMO_TODAY_ISO) {
  return VILLAS
    .map((v) => ({ villa: v, ...getVillaStateOn(v.villa_id, iso) }))
    .filter((row) => row.state === "OCCUPIED" || row.state === "ARRIVAL_TODAY");
}

// ---------------------------------------------------------------------------
// Timeline (for "Hoy" section)
// ---------------------------------------------------------------------------

/**
 * Ordered list of the day's operational events: checkouts (11:00), cleaning
 * (12:00), check-ins (14:00). Times are demo defaults — no real ops SLAs
 * modelled here.
 */
export function todayTimeline(iso = DEMO_TODAY_ISO) {
  const outs = departuresOn(iso).map((r) => ({
    time: "11:00", kind: "checkout", reservation: r, villa: getVilla(r.assigned_villa_id),
  }));
  const cleans = cleaningTasksOn(iso).map((row) => ({
    time: "12:00", kind: "cleaning", villa: row.villa, reservation: row.reservation || null,
  }));
  const ins = arrivalsOn(iso).map((r) => ({
    time: "14:00", kind: "checkin", reservation: r, villa: getVilla(r.assigned_villa_id),
  }));
  return [...outs, ...cleans, ...ins];
}

// ---------------------------------------------------------------------------
// Monthly metrics
// ---------------------------------------------------------------------------

/**
 * Aggregated month metrics — every screen KPI derives from this.
 *
 *   guest_nights            = sum of stay nights inside the month
 *   commercial_availability = total villa-nights − (owner + maintenance + admin blocks) inside month
 *   occupancy               = guest_nights / commercial_availability
 *   accommodation_revenue   = per-night prorated within month
 *   adr                     = accommodation_revenue / guest_nights
 *   revpar                  = accommodation_revenue / (villas × month_days)
 */
export function monthlyMetrics(monthPrefix = DEMO_MONTH) {
  const { days } = monthRange(monthPrefix);
  const villas = VILLAS.length;
  const totalAvail = villas * days;

  const blockedNights = getBlocks()
    .filter((b) => b.type !== "SYSTEM_BLOCK")
    .reduce(
      (acc, b) => acc + nightsInMonth(b.start_date, addDays(effectiveBlockEnd(b), 1), monthPrefix),
      0,
    );

  let guestNights = 0;
  let accRevenue = 0;
  const byChannel = {};
  const byPool = {};

  for (const r of activeReservations()) {
    const nInMonth = nightsInMonth(r.check_in, r.check_out, monthPrefix);
    if (nInMonth === 0) continue;
    const share = nInMonth / r.nights;
    const rev = r.accommodation_revenue_eur * share;
    guestNights += nInMonth;
    accRevenue += rev;
    byChannel[r.channel] = (byChannel[r.channel] || 0) + rev;
    byPool[r.pool_id] = (byPool[r.pool_id] || 0) + rev;
  }

  const commercialAvailability = totalAvail - blockedNights;
  const occupancy = commercialAvailability > 0 ? guestNights / commercialAvailability : 0;
  const adr = guestNights > 0 ? accRevenue / guestNights : 0;
  const revpar = totalAvail > 0 ? accRevenue / totalAvail : 0;

  return {
    monthPrefix,
    days,
    villas,
    totalAvail,
    blockedNights,
    commercialAvailability,
    guestNights,
    accommodationRevenue: accRevenue,
    occupancy,
    adr,
    revpar,
    byChannel,
    byPool,
  };
}

/** Channel mix (% of revenue in the month) sorted desc. */
export function channelMix(monthPrefix = DEMO_MONTH) {
  const { byChannel, accommodationRevenue } = monthlyMetrics(monthPrefix);
  const rows = Object.entries(byChannel).map(([key, revenue]) => ({
    channel: getChannel(key),
    revenue,
    share: accommodationRevenue > 0 ? revenue / accommodationRevenue : 0,
  }));
  return rows.sort((a, b) => b.revenue - a.revenue);
}

// ---------------------------------------------------------------------------
// Financial breakdown for a single reservation (demo P&L)
// ---------------------------------------------------------------------------

/**
 * Atoms per-reservation. Los conceptos "monthly-variable" (opex y
 * management) NO se calculan por reserva porque sólo tienen sentido a nivel
 * mensual (opex es un importe fijo del mes, management es 15 % sobre la base
 * post-opex del mes completo). Consumidores que necesiten el neto real
 * deben mirar `villaFinancialBreakdown(villa_id, month)`.
 */
export function reservationFinancials(reservation) {
  if (!reservation) return null;
  const rev = reservation.accommodation_revenue_eur || 0;
  const channel = getChannel(reservation.channel);
  const tax = rev * 0.10;
  const channelCost = rev * (channel.commission_pct || 0);
  const paymentProcessing = dokuCostForReservation(reservation);
  return {
    accommodation_revenue: rev,
    tax,
    channel_cost: channelCost,
    payment_processing: paymentProcessing,
  };
}

// ---------------------------------------------------------------------------
// Upcoming reservations (next N days from a reference date)
// ---------------------------------------------------------------------------

export function upcomingReservations(fromISO = DEMO_TODAY_ISO, limit = 8) {
  return activeReservations()
    .filter((r) => r.check_in >= fromISO)
    .sort((a, b) => (a.check_in < b.check_in ? -1 : 1))
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Statements helpers
// ---------------------------------------------------------------------------

export function statementsForVilla(villaId) {
  return MONTHLY_STATEMENTS.filter((s) => s.villa_id === villaId);
}

// ---------------------------------------------------------------------------
// Owner stay usage — nights consumed in the calendar year (approved + completed)
// ---------------------------------------------------------------------------

export function ownerStayUsage(villaId, year = 2028) {
  const prefix = String(year);
  const villaRequests = getOwnerStayRequests().filter(
    (r) => r.villa_id === villaId && r.start_date.startsWith(prefix),
  );
  // Sólo las aprobadas o completadas consumen las 60 noches. Pending y
  // rejected NO cuentan. Devolvemos `requests` con TODOS los estados de la
  // villa para que la UI pueda mostrar historial reactivo (Investor).
  const countingRequests = villaRequests.filter(
    (r) => r.status === "approved" || r.status === "completed",
  );
  const used = countingRequests.reduce((acc, r) => acc + (r.nights || 0), 0);
  return {
    used,
    limit: 60,
    remaining: Math.max(0, 60 - used),
    requests: villaRequests,
    counted_requests: countingRequests,
  };
}

// ---------------------------------------------------------------------------
// Owner-stay validation (documented rules, evaluated visually in demo)
// ---------------------------------------------------------------------------

/**
 * Returns per-rule checks so the UI can show *why* a request fails without
 * having to re-implement the policy in the component.
 *
 *   availability   : villa is free (no reservation, block, other owner stay)
 *                    for every night in the requested range.
 *   notice_ok      : requested_at ≥ 30 days before start_date.
 *   quota_ok       : approved + this-request nights ≤ 60 in the calendar year.
 *   blackout_ok    : no overlap with any SPECIAL_PERIODS `OWNER_STAY` block.
 */
export function ownerStayValidation(request, referenceISO = DEMO_TODAY_ISO) {
  const villaId = request.villa_id;
  const nights = datesBetweenExclusive(request.start_date, addDays(request.end_date, 1));

  // Availability: no reservation night in range AND no other block overlaps
  const villaReservations = reservationsForVilla(villaId);
  const villaBlocks = blocksForVilla(villaId).filter(
    (b) => b.block_id !== request.linked_block_id,
  );
  const conflicts = [];
  for (const iso of nights) {
    if (villaReservations.some((r) => isNightInStay(iso, r.check_in, r.check_out))) {
      conflicts.push(`${iso}: reserva`);
    }
    if (villaBlocks.some((b) => isDayInBlock(iso, b))) {
      conflicts.push(`${iso}: bloqueo existente`);
    }
  }

  // Notice: requested_at vs start_date
  let noticeDays = null;
  let noticeOk = false;
  if (request.requested_at) {
    const reqDate = new Date(request.requested_at);
    const startDate = parseISO(request.start_date);
    noticeDays = Math.floor((startDate.getTime() - reqDate.getTime()) / 86400000);
    noticeOk = noticeDays >= 30;
  }

  // Quota: nights already approved this year + this request
  const usage = ownerStayUsage(villaId, request.start_date.slice(0, 4));
  const includeThis = ["requested", "approved", "completed"].includes(request.status);
  const projected = usage.used + (includeThis && request.status === "requested" ? nights.length : 0);
  const quotaOk = projected <= 60;

  // Blackout: any SPECIAL_PERIODS with OWNER_STAY block overlapping
  let blackoutHit = null;
  for (const p of SPECIAL_PERIODS) {
    if (!(p.blocks || []).includes("OWNER_STAY")) continue;
    const overlap = nights.some((iso) => iso >= p.start_date && iso <= p.end_date);
    if (overlap) { blackoutHit = p; break; }
  }
  const blackoutOk = blackoutHit === null;

  const allOk = conflicts.length === 0 && noticeOk && quotaOk && blackoutOk;

  return {
    all_ok: allOk,
    availability_ok: conflicts.length === 0,
    conflicts,
    notice_days: noticeDays,
    notice_ok: noticeOk,
    quota_ok: quotaOk,
    quota_used_before: usage.used,
    quota_projected: projected,
    blackout_ok: blackoutOk,
    blackout: blackoutHit,
    nights: nights.length,
    referenceISO,
  };
}

// ---------------------------------------------------------------------------
// Property issues selectors
// ---------------------------------------------------------------------------

export function issuesByStatus(status) {
  if (status === "open_or_assigned") {
    return PROPERTY_ISSUES.filter((i) => i.status === "open" || i.status === "assigned");
  }
  return PROPERTY_ISSUES.filter((i) => i.status === status);
}

export function issuesForVilla(villaId) {
  return PROPERTY_ISSUES.filter((i) => i.villa_id === villaId);
}

export function openIssueCounts() {
  return {
    open: PROPERTY_ISSUES.filter((i) => i.status === "open").length,
    assigned: PROPERTY_ISSUES.filter((i) => i.status === "assigned").length,
    in_progress: PROPERTY_ISSUES.filter((i) => i.status === "in_progress").length,
    resolved: PROPERTY_ISSUES.filter((i) => i.status === "resolved").length,
  };
}

// ---------------------------------------------------------------------------
// Allocation ranking (Checkpoint B — Allocation Engine)
// ---------------------------------------------------------------------------

/**
 * Per-villa monthly figures used by the ranking and the simulator.
 *
 *   revenue_month     — accommodation revenue prorated inside the month
 *   guest_nights      — nights slept inside the month
 *   owner_days        — owner-stay nights inside the month (non-compensable)
 *   maintenance_days  — maintenance nights inside the month (compensable when
 *                       fairness_compensable=true on the block)
 *   commercial_nights — month days − blocks
 *   occupancy         — guest_nights / commercial_nights (0..1)
 */
export function villaMonthStats(villaId, monthPrefix = DEMO_MONTH) {
  const { days } = monthRange(monthPrefix);
  let revenue = 0;
  let guestNights = 0;
  for (const r of activeReservations()) {
    if (r.assigned_villa_id !== villaId) continue;
    const nInMonth = nightsInMonth(r.check_in, r.check_out, monthPrefix);
    if (nInMonth === 0) continue;
    guestNights += nInMonth;
    revenue += r.accommodation_revenue_eur * (nInMonth / r.nights);
  }
  let ownerDays = 0;
  let maintDaysCompensable = 0;
  let maintDaysNonCompensable = 0;
  for (const b of blocksForVilla(villaId)) {
    const n = nightsInMonth(b.start_date, addDays(effectiveBlockEnd(b), 1), monthPrefix);
    if (n === 0) continue;
    if (b.type === "OWNER_STAY") ownerDays += n;
    else if (b.type === "MAINTENANCE") {
      if (b.fairness_compensable) maintDaysCompensable += n;
      else maintDaysNonCompensable += n;
    }
  }
  const blockedDays = ownerDays + maintDaysCompensable + maintDaysNonCompensable;
  const commercialNights = Math.max(0, days - blockedDays);
  const occupancy = commercialNights > 0 ? guestNights / commercialNights : 0;
  return {
    villa_id: villaId,
    days,
    revenue_month: revenue,
    guest_nights: guestNights,
    owner_days: ownerDays,
    maintenance_days_compensable: maintDaysCompensable,
    maintenance_days_non_compensable: maintDaysNonCompensable,
    commercial_nights: commercialNights,
    occupancy,
  };
}

/**
 * Composite fairness score. Lower = the pool is under-serving this villa →
 * the allocation engine will steer the next reservation towards it.
 *
 *   weighted_perf = 0.7 · revenue_norm + 0.3 · occupancy_norm
 *   horizon       = 0.7 · month + 0.3 · YTD
 *
 * `revenue_norm` / `occupancy_norm` are normalized against the pool max
 * (so the leader → 1.0, others in [0..1]).
 */
export function allocationRanking(poolId, monthPrefix = DEMO_MONTH) {
  const villas = villasByPool(poolId);
  const rows = villas.map((v) => {
    const month = villaMonthStats(v.villa_id, monthPrefix);
    const ytd = YTD_STATS_2028[v.villa_id] || { revenue_eur: 0, guest_nights: 0 };
    return { villa: v, month, ytd };
  });

  const maxRevMonth = Math.max(...rows.map((r) => r.month.revenue_month), 1);
  const maxRevYtd = Math.max(...rows.map((r) => r.ytd.revenue_eur), 1);
  const maxOccMonth = Math.max(...rows.map((r) => r.month.occupancy), 0.0001);

  return rows.map((r) => {
    // Maintenance-compensable adjustment: if a villa lost inventory, boost
    // its "revenue per available night" so fairness doesn't punish it.
    const availAdjustment = r.month.maintenance_days_compensable > 0
      ? (r.month.days - r.month.maintenance_days_compensable) / r.month.days
      : 1;
    const revMonthNorm = (r.month.revenue_month / maxRevMonth) / (availAdjustment || 1);
    const revYtdNorm = r.ytd.revenue_eur / maxRevYtd;
    const occMonthNorm = r.month.occupancy / maxOccMonth;

    // 70% month · 30% YTD for the revenue lens; occupancy only uses month.
    const revenueComponent = 0.7 * revMonthNorm + 0.3 * revYtdNorm;
    const occupancyComponent = occMonthNorm;
    const performanceScore = 0.7 * revenueComponent + 0.3 * occupancyComponent;
    // fairness_index: 1 - performance, so 1 = most under-served.
    const fairnessIndex = 1 - performanceScore;
    return { ...r, performance_score: performanceScore, fairness_index: fairnessIndex, availability_adjustment: availAdjustment };
  }).sort((a, b) => a.performance_score - b.performance_score); // asc = fairness-preferred first
}

// ---------------------------------------------------------------------------
// Allocation simulation
// ---------------------------------------------------------------------------

/**
 * Simulate placing a hypothetical reservation of `revenue_eur / nights`
 * between `check_in` and `check_out` inside `pool_id`. Returns the eligible
 * villas (those fully available for the requested range) with their current
 * fairness score and the estimated post-reservation score.
 *
 * Tie breakers (per spec):
 *   1. Least future nights already booked (lower)
 *   2. Longer since last assignment (approximated by last check_in)
 *   3. villa_id deterministic
 */
export function simulateAllocation({
  pool_id, check_in, check_out, accommodation_revenue_eur,
  // Al reasignar una reserva existente hay que ignorar su propia ocupación:
  // si no, su unidad actual saldría excluida "por reserva existente" contra
  // sí misma.
  ignore_reservation_id = null,
}) {
  const currentRanking = allocationRanking(pool_id, DEMO_MONTH);
  const nights = nightsBetween(check_in, check_out);
  const nightsList = datesBetweenExclusive(check_in, check_out);

  // Split into eligible + excluded. Cada excluido lleva un motivo (reserva
  // existente, block por tipo). Motivos legibles para la UI.
  const excluded = [];
  const eligible = currentRanking.filter(({ villa }) => {
    const villaId = villa.villa_id;
    const reservations = reservationsForVilla(villaId);
    const villaBlocks = blocksForVilla(villaId);
    for (const iso of nightsList) {
      const conflictRes = reservations.find((r) => r.reservation_id !== ignore_reservation_id
        && isNightInStay(iso, r.check_in, r.check_out));
      if (conflictRes) {
        excluded.push({ villa, reason: "Reserva existente", date: iso, ref: conflictRes.reservation_id });
        return false;
      }
      const conflictBlock = villaBlocks.find((b) => isDayInBlock(iso, b));
      if (conflictBlock) {
        const reason = conflictBlock.type === "MAINTENANCE" ? "Maintenance"
          : conflictBlock.type === "OWNER_STAY" ? "Owner stay"
          : conflictBlock.type === "ADMIN_BLOCK" ? "Admin block"
          : conflictBlock.type;
        excluded.push({ villa, reason, date: iso, ref: conflictBlock.block_id });
        return false;
      }
    }
    return true;
  });

  // Attach per-villa preview
  const previews = eligible.map((row) => {
    const futureNights = reservationsForVilla(row.villa.villa_id)
      .filter((r) => r.check_in >= DEMO_TODAY_ISO)
      .reduce((acc, r) => acc + r.nights, 0);
    const lastCheckIn = reservationsForVilla(row.villa.villa_id)
      .map((r) => r.check_in)
      .sort()
      .pop() || "0000-00-00";
    // Estimated post-reservation performance: add the hypothetical revenue
    // to the villa's month revenue and re-normalize against the *current*
    // pool max (visual only).
    const currentMonthRev = row.month.revenue_month;
    const estimatedMonthRev = currentMonthRev + accommodation_revenue_eur;
    const poolMonthRevMax = Math.max(...currentRanking.map((r) => r.month.revenue_month), 1);
    const estimatedRevNorm = estimatedMonthRev / Math.max(poolMonthRevMax, estimatedMonthRev);
    const estimatedPerformance = 0.7 * (0.7 * estimatedRevNorm + 0.3 * (row.ytd.revenue_eur / Math.max(...currentRanking.map((r) => r.ytd.revenue_eur), 1)))
      + 0.3 * row.month.occupancy;
    return {
      villa: row.villa,
      current_score: row.performance_score,
      estimated_score: estimatedPerformance,
      score_delta: estimatedPerformance - row.performance_score,
      future_booked_nights: futureNights,
      last_check_in: lastCheckIn,
    };
  });

  // Sort by (current score asc) then tie-breakers
  previews.sort((a, b) => {
    if (a.current_score !== b.current_score) return a.current_score - b.current_score;
    if (a.future_booked_nights !== b.future_booked_nights) return a.future_booked_nights - b.future_booked_nights;
    if (a.last_check_in !== b.last_check_in) return a.last_check_in < b.last_check_in ? -1 : 1;
    return a.villa.villa_id < b.villa.villa_id ? -1 : 1;
  });

  return {
    pool_id,
    check_in,
    check_out,
    nights,
    accommodation_revenue_eur,
    eligible_count: previews.length,
    recommended: previews[0] || null,
    previews,
    excluded,
  };
}

// ---------------------------------------------------------------------------
// Allocation history — derived from reservations that carry allocation info
// ---------------------------------------------------------------------------

export function allocationHistory(monthPrefix = DEMO_MONTH) {
  const rows = activeReservations()
    .filter((r) => nightsInMonth(r.check_in, r.check_out, monthPrefix) > 0 && r.allocation)
    .map((r) => ({
      reservation_id: r.reservation_id,
      check_in: r.check_in,
      pool_id: r.pool_id,
      recommended_villa_id: r.allocation.recommended_villa_id,
      assigned_villa_id: r.assigned_villa_id,
      mode: r.allocation.mode,
      reason: r.allocation.manual_override?.reason || null,
      fairness_score: r.allocation.fairness_score,
    }))
    .sort((a, b) => (a.check_in < b.check_in ? 1 : -1)); // recent first
  const total = rows.length;
  const overrides = rows.filter((r) => r.mode === "manual_override").length;
  const overrideRate = total > 0 ? overrides / total : 0;
  return { rows, total, overrides, override_rate: overrideRate };
}

// ---------------------------------------------------------------------------
// Housekeeping — derive initial task list for a given day
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Investor-facing selectors (Checkpoint C — Reservas & Rendimiento)
// ---------------------------------------------------------------------------

/**
 * Payment processing demo por reserva.
 *   OTA (booking / airbnb) → 0 (procesa la OTA, no pasa por nuestra pasarela)
 *   payment_method NO explícito → 0 (no asumimos card por defecto)
 *   payment_method === "card"  → revenue × 2,8 % + Rp2.000 / DEMO_IDR_PER_EUR
 *   payment_method !== "card"  → 0 (bank_transfer / cash / etc.)
 */
export function dokuCostForReservation(reservation) {
  if (!reservation) return 0;
  const channel = getChannel(reservation.channel);
  if (channel.kind === "ota") return 0;
  if (reservation.payment_method !== "card") return 0;
  const rev = reservation.accommodation_revenue_eur || 0;
  const fixedEur = DEMO_DOKU_CARD.fixed_idr / DEMO_IDR_PER_EUR;
  return rev * DEMO_DOKU_CARD.percentage + fixedEur;
}

/**
 * P&L consolidado del mes para una villa concreta.
 *
 * Fórmula demo aprobada:
 *   management_base = accommodation − tax − channel_cost − payment_processing − opex
 *   management_fee  = management_base × 0,15
 *   owner_net       = management_base − management_fee
 *
 * `opex` NUNCA es un porcentaje del revenue: se toma del map centralizado
 * `OPERATING_EXPENSES_ESTIMATED[monthPrefix][villaId]` (mes en curso). En el
 * futuro el `monthly_villa_statement` cerrado sustituirá el estimated por
 * `operating_expenses_final_eur`.
 */
export function villaFinancialBreakdown(villaId, monthPrefix = DEMO_MONTH) {
  let accommodation = 0;
  let tax = 0;
  let channelCost = 0;
  let paymentProcessing = 0;
  for (const r of activeReservations()) {
    if (r.assigned_villa_id !== villaId) continue;
    const nInMonth = nightsInMonth(r.check_in, r.check_out, monthPrefix);
    if (nInMonth === 0) continue;
    const share = nInMonth / r.nights;
    const fin = reservationFinancials(r);
    accommodation += fin.accommodation_revenue * share;
    tax += fin.tax * share;
    channelCost += fin.channel_cost * share;
    paymentProcessing += fin.payment_processing * share;
  }
  const opex = (OPERATING_EXPENSES_ESTIMATED[monthPrefix] || {})[villaId] || 0;
  const managementBase = accommodation - tax - channelCost - paymentProcessing - opex;
  const management = managementBase * 0.15;
  const ownerNet = managementBase - management;
  return {
    accommodation_revenue: accommodation,
    tax,
    channel_cost: channelCost,
    payment_processing: paymentProcessing,
    opex,                 // mes en curso: estimated · cerrado: final (dataset-driven)
    opex_kind: "estimated",
    management_base: managementBase,
    management,
    owner_net_estimated: ownerNet,
  };
}

/**
 * Mix de canales para una sola villa en un mes. Devuelve un array ordenado
 * desc por revenue, con `share` (fracción 0..1) y `nights` (noches
 * prorrateadas dentro del mes).
 */
export function villaChannelMix(villaId, monthPrefix = DEMO_MONTH) {
  const bucket = {};
  let total = 0;
  for (const r of activeReservations()) {
    if (r.assigned_villa_id !== villaId) continue;
    const nInMonth = nightsInMonth(r.check_in, r.check_out, monthPrefix);
    if (nInMonth === 0) continue;
    const share = nInMonth / r.nights;
    const rev = r.accommodation_revenue_eur * share;
    if (!bucket[r.channel]) bucket[r.channel] = { revenue: 0, nights: 0 };
    bucket[r.channel].revenue += rev;
    bucket[r.channel].nights += nInMonth;
    total += rev;
  }
  return Object.entries(bucket)
    .map(([key, v]) => ({
      channel: getChannel(key),
      revenue: v.revenue,
      nights: v.nights,
      share: total > 0 ? v.revenue / total : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);
}

/**
 * Próximas reservas de la villa (para el investor). Filtra check_in ≥ fromISO
 * y ordena ascendente. No incluye datos de huésped fuera de counts.
 */
export function villaUpcomingReservations(villaId, fromISO = DEMO_TODAY_ISO, limit = 5) {
  return activeReservations()
    .filter((r) => r.assigned_villa_id === villaId && r.check_in >= fromISO)
    .sort((a, b) => (a.check_in < b.check_in ? -1 : 1))
    .slice(0, limit);
}

/**
 * Noches y accommodation revenue ya reservados en los próximos `days` días.
 * Wording para el investor: "Actualmente reservado" — nunca "previsto" ni
 * "garantizado".
 */
export function villaForwardBookings(villaId, fromISO = DEMO_TODAY_ISO, days = 90) {
  const toISO = addDays(fromISO, days);
  let nights = 0;
  let revenue = 0;
  for (const r of activeReservations()) {
    if (r.assigned_villa_id !== villaId) continue;
    const overlapStart = r.check_in > fromISO ? r.check_in : fromISO;
    const overlapEnd = r.check_out < toISO ? r.check_out : toISO;
    if (overlapEnd <= overlapStart) continue;
    const nInRange = datesBetweenExclusive(overlapStart, overlapEnd).length;
    if (nInRange === 0) continue;
    nights += nInRange;
    revenue += r.accommodation_revenue_eur * (nInRange / r.nights);
  }
  return { villa_id: villaId, from: fromISO, to: toISO, nights, revenue };
}

/**
 * YTD combinado: histórico ilustrativo de YTD_STATS_2028 (enero-julio) +
 * las cifras reales del mes en curso. Sirve para el bloque "YTD" del
 * investor sin duplicar cálculos.
 */
export function villaYtdCombined(villaId, monthPrefix = DEMO_MONTH) {
  const priorYtd = YTD_STATS_2028[villaId] || {
    revenue_eur: 0, guest_nights: 0, owner_nights: 0, maintenance_nights: 0,
  };
  const month = villaMonthStats(villaId, monthPrefix);
  const revenue = priorYtd.revenue_eur + month.revenue_month;
  const nights = priorYtd.guest_nights + month.guest_nights;
  const availableFromYtd = 212 - (priorYtd.owner_nights + priorYtd.maintenance_nights); // Jan-Jul (7 months × ≈30.3d) commercial approx
  const availableThisMonth = month.commercial_nights;
  const commercialNights = Math.max(1, availableFromYtd + availableThisMonth);
  return {
    villa_id: villaId,
    revenue,
    guest_nights: nights,
    commercial_nights: commercialNights,
    occupancy: nights / commercialNights,
    adr: nights > 0 ? revenue / nights : 0,
    revpar: commercialNights > 0 ? revenue / commercialNights : 0,
    prior_ytd: priorYtd,
    current_month: month,
  };
}

/**
 * Serie mensual para la gráfica de evolución del investor. Los meses de
 * enero–julio se derivan del acumulado YTD_STATS_2028 con un reparto
 * ligeramente variable (weights hardcoded pero coherentes entre villas) para
 * que la gráfica no sea plana. Agosto usa las cifras reales del dataset.
 *
 * Devuelve `[{ month, label, revenue, occupancy, owner_net_estimated,
 * owner_net_missing_reason }]`. `owner_net_estimated` es `null` en los meses
 * sin desglose de costes: son dato AUSENTE, no dato cero.
 */
const MONTH_LABELS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];
const PRIOR_MONTH_WEIGHTS = [0.11, 0.10, 0.12, 0.14, 0.16, 0.18, 0.19]; // Ene..Jul; suma ≈ 1.0
const PRIOR_MONTH_OCC     = [0.42, 0.48, 0.55, 0.58, 0.62, 0.65, 0.70];

/**
 * Razón por la que un mes no tiene neto del propietario.
 *
 * El neto aprobado es `base × 15%`, con base = alojamiento − tasa − canal −
 * procesamiento − opex. Para enero–julio no hay ninguno de esos costes:
 * `YTD_STATS_2028` solo trae ingresos y noches. Agosto sí tiene el desglose y
 * pasa por la cascada real.
 *
 * Aquí hubo durante mucho tiempo un recorte del 45% a ojo sobre el bruto,
 * duplicado además en el KPI del portal. Ponerle un nombre no lo
 * arreglaba: seguía siendo un neto inventado delante del inversor, en la
 * misma pantalla donde la tabla deduce un 15%. Un mes sin costes no tiene un
 * neto aproximado: no tiene neto. Se dice, y se dice por qué.
 */
export const OWNER_NET_MISSING_REASON = "missing_cost_inputs";

export function villaMonthlyEvolution(villaId) {
  const ytd = YTD_STATS_2028[villaId] || { revenue_eur: 0, guest_nights: 0 };
  const rows = [];
  for (let m = 0; m < 7; m++) {
    const revenue = ytd.revenue_eur * PRIOR_MONTH_WEIGHTS[m];
    const occupancy = PRIOR_MONTH_OCC[m];
    rows.push({
      month: `2028-${String(m + 1).padStart(2, "0")}`,
      label: MONTH_LABELS_ES[m],
      revenue,
      occupancy,
      // Ingresos y ocupación sí se derivan del acumulado; el neto NO. Es
      // `null` a propósito, no 0: cero sería una afirmación.
      owner_net_estimated: null,
      owner_net_missing_reason: OWNER_NET_MISSING_REASON,
    });
  }
  // Agosto — real desde el dataset.
  const augStats = villaMonthStats(villaId, DEMO_MONTH);
  const augFin = villaFinancialBreakdown(villaId, DEMO_MONTH);
  rows.push({
    month: DEMO_MONTH,
    label: MONTH_LABELS_ES[7],
    revenue: augStats.revenue_month,
    occupancy: augStats.occupancy,
    owner_net_estimated: augFin.owner_net_estimated,
    owner_net_missing_reason: null,
  });
  return rows;
}

/**
 * Neto acumulado del propietario en el año, para el bloque YTD del inversor.
 *
 * Devuelve la forma completa, no un número, porque la respuesta honesta casi
 * nunca es un número: mientras haya un solo mes sin desglose de costes, el
 * acumulado del año NO existe. Sumar los meses modelados y enseñar el
 * resultado como «Neto propietario» sería peor que el 0,55 que había antes —
 * una cifra que parece completa y se queda corta, sin nada que lo advierta.
 *
 *   { value, complete, modelled_months, unmodelled_months, reason }
 *
 * `value` solo trae número cuando `complete` es cierto. Si no, es `null` y
 * `reason` dice por qué, con el mismo vocabulario que usa el backend en
 * `roi_tracking_logic` (`missing_cost_inputs`): un hueco se explica igual en
 * los dos lados o acaban siendo dos huecos distintos.
 */
export function villaYtdOwnerNet(villaId) {
  const rows = villaMonthlyEvolution(villaId);
  const modelled = rows.filter((r) => r.owner_net_estimated != null);
  const unmodelled = rows.length - modelled.length;
  if (unmodelled > 0) {
    return {
      value: null,
      complete: false,
      modelled_months: modelled.length,
      unmodelled_months: unmodelled,
      reason: OWNER_NET_MISSING_REASON,
    };
  }
  return {
    value: modelled.reduce((acc, r) => acc + r.owner_net_estimated, 0),
    complete: true,
    modelled_months: modelled.length,
    unmodelled_months: 0,
    reason: null,
  };
}

/**
 * For every villa, decide the housekeeping starting state for `iso`. When a
 * checkout happens today and a check-in follows, the turnaround is tight and
 * priority = "high". When the villa was already vacant we don't emit a task.
 */
export function housekeepingTasksForDay(iso = DEMO_TODAY_ISO) {
  const out = [];
  for (const v of VILLAS) {
    const arriving = reservationsForVilla(v.villa_id).find((r) => r.check_in === iso);
    const departing = reservationsForVilla(v.villa_id).find((r) => r.check_out === iso);
    const inStay = reservationsForVilla(v.villa_id).find(
      (r) => isNightInStay(iso, r.check_in, r.check_out),
    );
    // Cases:
    // 1. Departure today, arrival today (same-day turnaround) → PENDING · high
    // 2. Departure today, no arrival today                    → PENDING · medium
    // 3. Arrival today, no departure (villa vacated day-before) → READY · low
    // 4. Otherwise                                            → no task
    if (departing && arriving) {
      out.push({
        villa: v, initial_status: "PENDING", priority: "high",
        checkout_res: departing, next_res: arriving,
        turnaround_hours: 3, // Demo default 11:00 → 14:00
      });
    } else if (departing) {
      out.push({
        villa: v, initial_status: "PENDING", priority: "medium",
        checkout_res: departing, next_res: null, turnaround_hours: null,
      });
    } else if (arriving && !inStay) {
      // Vacant → arriving today. Assume already inspected the day before.
      out.push({
        villa: v, initial_status: "READY", priority: "low",
        checkout_res: null, next_res: arriving, turnaround_hours: null,
      });
    }
  }
  return out;
}
